import classnames from 'classnames';
import {Link} from 'react-router';
import React from 'react';

import type {ClassroomModelV1, SubjectModelV2} from '@albert-io/models';
import {Heading, Text} from '@albert-io/atomic';

import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import withTracking from 'lib/hocs/withTracking';

import SubjectListCardActions from './SubjectsListCardActions';

import './teacher-subjects-list.scss';

const TrackedLink = withTracking(Link, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects Tab',
    component: 'Subject Card',
    content: (e) => e.target.dataset.trackingSubject
  }
});

interface Props {
  /** Powers the actions bar; hidden cards render their static bottom without one (cross-class view). */
  classroom?: ClassroomModelV1;
  isAdaptive: boolean;
  isHidden: boolean;
  handleCloseAccess?: (subject: SubjectModelV2) => void;
  handleOpenAccess?: (subject: SubjectModelV2) => void;
  handlePartialCreditToggled?: (classroomId: string, subject: SubjectModelV2) => void;
  showActions: boolean;
  subject: SubjectModelV2;
}

const noop = () => {};

export const SubjectsListCard = ({
  classroom,
  isAdaptive,
  isHidden,
  handleCloseAccess = noop,
  handleOpenAccess = noop,
  handlePartialCreditToggled = noop,
  showActions,
  subject
}: Props) => {
  const makeSubjectCardHref = () =>
    isAdaptive ? `/adaptive-subjects/${subject.getUrlSlug()}` : `/${subject.getUrlSlug()}`;

  const SubjectSplitCardTopWrapper = isHidden ? 'div' : TrackedLink;
  const curriculumAreaSlug = subject.getCurriculumAreaSlug();

  return (
    <SubjectSplitCard
      className='subject-split-card--action-bar-in-view teacher-subjects-list__course no-underline'
      key={subject.getId()}
    >
      <SubjectSplitCardTopWrapper
        to={makeSubjectCardHref()}
        className='no-underline'
        data-tracking-subject={subject.getName()}
      >
        <SubjectSplitCard.Top
          className={classnames(
            'teacher-subject-list__card__link flex items-end overflow-hidden px-x3 py-x3 content-primary-inverse no-underline',
            {
              [`background-domain-${curriculumAreaSlug}`]: !isHidden,
              'teacher-subject-list__card__link--hidden': isHidden
            }
          )}
        >
          {!isAdaptive && <SubjectSplitCard.Thumbnail xlinkHref={subject.getLogoLocation().href} />}
          <SubjectSplitCard.Title className='flex flex-col'>
            <Heading className='teacher-subject-list__card__link__title'>
              {subject.getName()}
            </Heading>
          </SubjectSplitCard.Title>
        </SubjectSplitCard.Top>
      </SubjectSplitCardTopWrapper>
      {/* Hidden bottom is intrinsic: hidden subjects have no actions, and gating the label behind
          showActions left unlicensed teachers with only the grey top as a cue. */}
      {isHidden ? (
        <SubjectSplitCard.Bottom
          className='subject-split-card__action-bar flex px-x2! py-x1!'
          paddingLevel={0}
          roundness='none'
        >
          <Text bold color='tertiary' size='s'>
            Hidden Subject
          </Text>
        </SubjectSplitCard.Bottom>
      ) : (
        showActions &&
        classroom && (
          <SubjectListCardActions
            classroom={classroom}
            handleCloseAccess={handleCloseAccess}
            handleOpenAccess={handleOpenAccess}
            handlePartialCreditToggled={handlePartialCreditToggled}
            subject={subject}
          />
        )
      )}
    </SubjectSplitCard>
  );
};
